import { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/RichTextEditor';
import { articleHtml, sanitize } from '@/lib/articleBody';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Star, Upload, Play, EyeOff } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (p) => (!p ? null : p.startsWith('http') ? p : `${BACKEND}${p}`);
const CATS = ['Nutrition', 'Training', 'Recovery', 'Mindset', 'Longevity', 'Supplements', 'Breathing', "Coach's Corner"];
const EMPTY = { title: '', category: 'Nutrition', excerpt: '', body: '', body_html: '', body_json: null, image_url: '', youtube_url: '', published: true, featured: false, order: 0 };

export default function AdminAcademy() {
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => { const r = await api.get('/articles'); setArticles(r.data); return r.data; };
  useEffect(() => {
    load().then(list => {
      const id = new URLSearchParams(window.location.search).get('edit');
      const a = id && list.find(x => x.id === id);
      if (a) openEdit(a);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setEditId(null); setF(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditId(a.id); setF({ ...EMPTY, ...a, body_html: a.body_html || articleHtml(a) }); setOpen(true); };

  const save = async () => {
    if (!f.title.trim()) return toast.error('Title required');
    try {
      const payload = { ...f, body_html: sanitize(f.body_html), order: Number(f.order) || 0 };
      if (editId) await api.put(`/articles/${editId}`, payload);
      else await api.post('/articles', payload);
      setOpen(false); load(); toast.success(editId ? 'Updated' : 'Article published');
    } catch { toast.error('Failed'); }
  };
  const del = async (id) => { if (!confirm('Delete this article?')) return; await api.delete(`/articles/${id}`); load(); };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/articles/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setF(prev => ({ ...prev, image_url: r.data.image_url })); toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-academy">
      <div className="flex items-end justify-between">
        <div><div className="label-mini text-[#FF6FA8]">Content</div><h1 className="font-display font-black text-4xl mt-1">Academy CMS</h1>
          <p className="text-sm text-muted-foreground mt-1">{articles.length} articles · Coach's Corner & 7 topics</p></div>
        <Button onClick={openNew} data-testid="add-article-btn" className="bg-[#FF6FA8] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-2" /> New Article</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {articles.map(a => (
          <div key={a.id} className="regen-card-elevated overflow-hidden group" data-testid={`article-${a.id}`}>
            <div className="relative h-32 bg-[var(--c-film)]">
              {imgSrc(a.image_url) && <img src={imgSrc(a.image_url)} alt={a.title} className="w-full h-full object-cover" />}
              {a.youtube_url && <div className="absolute inset-0 flex items-center justify-center bg-black/30"><Play className="w-6 h-6 text-[var(--c-ink)]" /></div>}
              <div className="absolute top-2 left-2 flex gap-1.5">
                {a.featured && <span className="pill bg-[#FF6FA8] text-[#081726] text-[10px] font-bold flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> Featured</span>}
                {!a.published && <span className="pill bg-[var(--c-film)] text-[var(--c-ink)] text-[10px] font-bold flex items-center gap-0.5"><EyeOff className="w-2.5 h-2.5" /> Draft</span>}
              </div>
            </div>
            <div className="p-4">
              <div className="label-mini text-[#FF6FA8]">{a.category}</div>
              <div className="font-display font-bold mt-1 line-clamp-1">{a.title}</div>
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.excerpt || a.body}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-article-${a.id}`} className="h-8 px-2 hover:bg-[var(--c-film)]"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => del(a.id)} data-testid={`delete-article-${a.id}`} className="h-8 px-2 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="article-title" className="bg-transparent border-[var(--c-hair)]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={f.category} onValueChange={v => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="article-category" className="bg-transparent border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Order</Label><Input type="number" value={f.order} onChange={e => setF({ ...f, order: e.target.value })} className="bg-transparent border-[var(--c-hair)]" /></div>
            </div>
            <div><Label>Excerpt</Label><Input value={f.excerpt} onChange={e => setF({ ...f, excerpt: e.target.value })} data-testid="article-excerpt" placeholder="One-line summary" className="bg-transparent border-[var(--c-hair)]" /></div>

            <div>
              <Label>Cover Image</Label>
              <div className="flex items-center gap-3 mt-1">
                {imgSrc(f.image_url) && <img src={imgSrc(f.image_url)} alt="cover" className="w-16 h-16 rounded-lg object-cover" />}
                <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="article-image-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-[var(--c-hair)]"><Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading…' : 'Upload'}</Button>
              </div>
              <Input value={f.image_url} onChange={e => setF({ ...f, image_url: e.target.value })} placeholder="…or paste an image URL" className="bg-transparent border-[var(--c-hair)] mt-2 text-xs" />
            </div>

            <div><Label>YouTube URL (optional)</Label><Input value={f.youtube_url} onChange={e => setF({ ...f, youtube_url: e.target.value })} data-testid="article-youtube" placeholder="https://youtu.be/…" className="bg-transparent border-[var(--c-hair)]" /></div>
            <div><Label>Body</Label>
              <div className="mt-1"><RichTextEditor key={`${editId || 'new'}-${open}`} initialContent={f.body_json || f.body_html}
                onChange={({ html, json, text }) => setF(prev => ({ ...prev, body_html: html, body_json: json, body: text }))} /></div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2"><Switch checked={f.published} onCheckedChange={v => setF({ ...f, published: v })} data-testid="article-published" /><span className="text-sm">Published</span></div>
              <div className="flex items-center gap-2"><Switch checked={f.featured} onCheckedChange={v => setF({ ...f, featured: v })} data-testid="article-featured" /><span className="text-sm">Featured</span></div>
            </div>

            <Button onClick={save} data-testid="article-save" className="w-full bg-[#FF6FA8] text-[#081726] font-semibold">{editId ? 'Save Changes' : 'Publish Article'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
